import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronDownIcon,
  UserGroupIcon,
  VideoCameraIcon,
  CheckIcon,
  LockClosedIcon,
  InformationCircleIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { useApi, type User, type Group, type CameraRef } from '../api';
import { usePermissions } from '../auth';
import GroupPickerPanel from '../components/groups/GroupPickerPanel';
import { PageTitle } from '../components/layout/TitleBar';
import { BTN_PRIMARY_SM } from '../components/common/ui';
import { FilterColumn, FilterSearch, PageWithFilters, useFilterColumn } from '../components/layout/FilterColumn';
import FillHeightCard, { TABLE_HEAD, TABLE_ROW, TABLE_TH, tableHeadRow } from '../components/layout/FillHeightCard';

export interface AccessControlPageProps {
  /** Extra classes on the page body (the PageWithFilters row). */
  className?: string;
  /** Require a selected node (cloud, multi-node). When set with no nodeKey, prompts to pick one. */
  requireNode?: boolean;
  /** The selected node id (cloud). Changing it refetches; adapters bind it internally. */
  nodeKey?: string | null;
  /** Label for the node concept when node-scoped (cloud). */
  nodeLabel?: string;
  /** Permission required to save; when set, the Save button is gated on it. */
  editPermission?: string;
}

/** Flatten a (possibly nested) group list into the flat `parent_id` list the picker wants. */
function flattenGroups(nodes: Group[]): Group[] {
  const out: Group[] = [];
  const walk = (list: Group[]) => {
    for (const n of list) {
      out.push(n);
      if (n.children && n.children.length) walk(n.children);
    }
  };
  walk(Array.isArray(nodes) ? nodes : []);
  return out;
}

/**
 * Access Control — assign camera GROUPS and individual CAMERAS to a user.
 * A user's accessible cameras = union(group-derived, directly-assigned), enforced
 * by the backend for the camera list, live (ULLFU) and recordings.
 *
 * Shared by both dashboards via useApi().accessControl. Single-node hosts (local)
 * omit `listNodes`; multi-node hosts (cloud) provide it and the page shows a node
 * picker, threading the selected node id through every call.
 */
export default function AccessControlPage({
  className = '',
  requireNode = false,
  nodeKey,
  nodeLabel = 'Node',
  editPermission,
}: AccessControlPageProps) {
  const { accessControl: api } = useApi();
  const { hasPermission } = usePermissions();
  const canEdit = !editPermission || hasPermission(editPermission);
  const noNode = requireNode && !nodeKey;

  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [allCameras, setAllCameras] = useState<CameraRef[]>([]);
  const [cameraSearch, setCameraSearch] = useState('');

  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [cameraIds, setCameraIds] = useState<string[]>([]);
  // Cameras reachable through the selected groups (incl. sub-groups) — locked,
  // mirroring the "inherited from group" lock on the Model Assignment page.
  const [groupCameraIds, setGroupCameraIds] = useState<Set<string>>(new Set());

  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  // The user column's open/folded state, remembered for this page.
  const userCol = useFilterColumn('access-control');

  // ── Load users + groups + cameras (refetch when the selected node changes) ──
  useEffect(() => {
    setSelectedUser(null);
    setGroupIds([]); setCameraIds([]); setGroupCameraIds(new Set());
    if (noNode) { setUsers([]); setAllGroups([]); setAllCameras([]); return; }
    setLoadingLists(true);
    Promise.all([api.listUsers(), api.listGroups(), api.listCameras()])
      .then(([us, gs, cs]) => {
        // Active users only — deactivated/deleted users are not assignable.
        setUsers((us || []).filter((u) => u.is_active !== false));
        setAllGroups(flattenGroups(gs || []));
        setAllCameras(cs || []);
      })
      .catch(() => setFeedback({ kind: 'err', msg: 'Failed to load access data' }))
      .finally(() => setLoadingLists(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey, noNode]);

  // ── Load the selected user's current access ──
  const selectUser = useCallback((user: User) => {
    setSelectedUser(user);
    setDirty(false);
    setFeedback(null);
    setLoadingAccess(true);
    api.getAccess(user.id)
      .then((sel) => {
        setGroupIds(sel.groupIds || []);
        setCameraIds(sel.cameraIds || []);
      })
      .catch(() => setFeedback({ kind: 'err', msg: 'Failed to load user access' }))
      .finally(() => setLoadingAccess(false));
  }, [api]);

  // ── Resolve selected groups → their cameras (recursive) so we can lock them ──
  useEffect(() => {
    if (groupIds.length === 0) { setGroupCameraIds(new Set()); return; }
    let cancelled = false;
    Promise.all(groupIds.map((gid) => api.groupCameras(gid).catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        setGroupCameraIds(new Set(lists.flat().map((c: CameraRef) => c.id)));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIds]);

  const onGroupsChange = (ids: string[]) => { setGroupIds(ids); setDirty(true); };
  const toggleCamera = (id: string) => {
    if (groupCameraIds.has(id)) return; // locked — covered by a group
    setCameraIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
    setDirty(true);
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, userSearch]);

  const filteredCameras = useMemo(() => {
    const q = cameraSearch.trim().toLowerCase();
    if (!q) return allCameras;
    return allCameras.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [allCameras, cameraSearch]);

  const directCameraCount = useMemo(
    () => cameraIds.filter((id) => !groupCameraIds.has(id)).length,
    [cameraIds, groupCameraIds],
  );

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setFeedback(null);
    try {
      // Don't persist direct grants for cameras already covered by a group.
      const directCameraIds = cameraIds.filter((id) => !groupCameraIds.has(id));
      await api.setAccess(selectedUser.id, { groupIds, cameraIds: directCameraIds });
      setDirty(false);
      setFeedback({ kind: 'ok', msg: `Access updated for ${selectedUser.name || selectedUser.email}` });
    } catch (e: any) {
      setFeedback({ kind: 'err', msg: e?.message || 'Failed to save access' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle title="Access Control" subtitle="Users see and access only the camera groups and cameras assigned to them" />

      {noNode ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          <ServerStackIcon className="h-5 w-5" />
          Select a {nodeLabel.toLowerCase()} to manage its users' camera access.
        </div>
      ) : (
      <PageWithFilters
        className={className}
        // The user picker is the page's side column — flush, foldable, remembered — as the
        // filters are on the Alerts page. Pick a user there; edit their access on the right.
        filters={(
          <FilterColumn
            width="wide"
            open={userCol.open}
            onToggle={userCol.toggle}
            label="Users"
            activeCount={userSearch ? 1 : 0}
            onClearAll={() => setUserSearch('')}
          >
            <div data-tour="access-control-users" className="space-y-3">
              <FilterSearch value={userSearch} onChange={setUserSearch} placeholder="Search users…" />
              <div className="-mx-2 space-y-0.5">
                {loadingLists && <p className="px-2 py-3 text-[13px] text-muted-foreground">Loading…</p>}
                {!loadingLists && filteredUsers.length === 0 && (
                  <p className="px-2 py-3 text-[13px] text-muted-foreground">No users found.</p>
                )}
                {filteredUsers.map((u) => {
                  const active = selectedUser?.id === u.id;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => selectUser(u)}
                      aria-current={active ? 'true' : undefined}
                      className={`w-full rounded-r-md px-2 py-2 text-left transition-colors ${
                        active ? 'bg-brand-light' : 'hover:bg-muted'
                      }`}
                      style={{ borderLeft: `3px solid ${active ? 'var(--brand)' : 'transparent'}` }}
                    >
                      <div className={`truncate text-[13px] font-medium ${active ? 'text-brand' : 'text-foreground'}`}>{u.name || u.email}</div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {u.email}{u.role ? ` · ${u.role}` : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </FilterColumn>
        )}
      >
        {/* ── How it works — compact, folded open on demand ── */}
        <details data-tour="access-control-help" className="group rounded-lg border border-border bg-card px-3 py-2 text-[12px] text-muted-foreground shadow-card">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 font-medium text-foreground [&::-webkit-details-marker]:hidden">
            <InformationCircleIcon className="h-4 w-4 shrink-0" /> How access works
            <span className="ml-1 truncate font-normal text-muted-foreground">
              — groups' cameras + directly-assigned cameras
            </span>
            <ChevronDownIcon className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>A user's accessible cameras = <span className="font-medium">cameras from assigned groups</span> + <span className="font-medium">directly-assigned cameras</span>. Enforced for the camera list, live view, and recordings.</li>
            <li>Assigning a <span className="font-medium">parent group</span> automatically includes all of its sub-groups and their cameras.</li>
            <li>Cameras covered by an assigned group show as
              <LockClosedIcon className="mx-1 inline h-3 w-3 align-text-bottom" /><span className="font-medium">via group</span> and are locked —
              to remove one, remove it from the group rather than toggling it here.</li>
            <li>Only <span className="font-medium">active</span> users appear here.{requireNode ? ` These are the cloud users granted access to this ${nodeLabel.toLowerCase()}.` : ''}</li>
          </ul>
        </details>

        {/* ── The access editor — one card filling the rest of the screen ── */}
        <div data-tour="access-control-editor">
          {!selectedUser ? (
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-border p-10 text-sm text-muted-foreground">
              Select a user to manage their camera access.
            </div>
          ) : (
            <FillHeightCard>
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">
                    Access for {selectedUser.name || selectedUser.email}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {groupIds.length} group{groupIds.length === 1 ? '' : 's'}
                    {groupCameraIds.size > 0 && <> · {groupCameraIds.size} via group{groupCameraIds.size === 1 ? '' : 's'}</>}
                    {' · '}{directCameraCount} direct camera{directCameraCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {feedback && (
                    <span className={`text-[11px] ${feedback.kind === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                      {feedback.msg}
                    </span>
                  )}
                  <button
                    onClick={save}
                    disabled={!dirty || saving || loadingAccess || !canEdit}
                    title={!canEdit ? 'You do not have permission to edit access' : undefined}
                    className={BTN_PRIMARY_SM}
                  >
                    <CheckIcon className="h-4 w-4" />
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>

              {loadingAccess ? (
                <div className="p-6 text-sm text-muted-foreground">Loading access…</div>
              ) : (
                // Stacked (and scrolling together) on narrow screens; side by side from xl,
                // each half scrolling on its own so the cameras' header stays put.
                <div className="min-h-0 flex-1 overflow-auto xl:grid xl:grid-cols-2 xl:divide-x xl:divide-border xl:overflow-hidden">
                  {/* Groups */}
                  <div className="flex flex-col xl:min-h-0">
                    <div className="flex shrink-0 items-center gap-1.5 border-b border-border px-4 py-2.5 text-[13px] font-semibold text-foreground">
                      <UserGroupIcon className="h-4 w-4" /> Camera Groups
                    </div>
                    <div className="p-4 xl:min-h-0 xl:flex-1 xl:overflow-auto">
                      <GroupPickerPanel
                        groups={allGroups}
                        selectedIds={groupIds}
                        onChange={onGroupsChange}
                        label="Group Access"
                        multiple
                      />
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Access to a parent group automatically includes all its sub-groups.
                      </p>
                    </div>
                  </div>

                  {/* Cameras — a search strip, then the list with a sticky header */}
                  <div className="flex flex-col border-t border-border xl:min-h-0 xl:border-t-0">
                    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
                      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
                        <VideoCameraIcon className="h-4 w-4" /> Individual Cameras
                      </span>
                      <div className="ml-auto flex gap-2 text-[11px]">
                        <button type="button" className="text-brand hover:underline"
                          onClick={() => { setCameraIds(allCameras.map((c) => c.id).filter((id) => !groupCameraIds.has(id))); setDirty(true); }}>
                          Select all
                        </button>
                        <button type="button" className="text-muted-foreground hover:underline"
                          onClick={() => { setCameraIds([]); setDirty(true); }}>
                          Clear
                        </button>
                      </div>
                      <div className="w-full">
                        <FilterSearch value={cameraSearch} onChange={setCameraSearch} placeholder="Search cameras…" />
                      </div>
                    </div>
                    <div className="xl:min-h-0 xl:flex-1 xl:overflow-auto">
                      <table className="w-full text-sm">
                        <thead className={TABLE_HEAD}>
                          {/* A search strip sits above it inside the card → the muted head. */}
                          <tr className={tableHeadRow(true)}>
                            <th className={`${TABLE_TH} w-10`}><span className="sr-only">Assigned</span></th>
                            <th className={TABLE_TH}>Camera</th>
                            <th className={`${TABLE_TH} text-right`}>Access</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCameras.length === 0 && (
                            <tr><td colSpan={3} className="px-3 py-6 text-center text-sm text-muted-foreground">No cameras found.</td></tr>
                          )}
                          {filteredCameras.map((c) => {
                            const locked = groupCameraIds.has(c.id);
                            const on = locked || cameraIds.includes(c.id);
                            return (
                              <tr
                                key={c.id}
                                title={locked ? 'Included via an assigned group — manage it from the group' : c.name}
                                onClick={() => toggleCamera(c.id)}
                                className={`${TABLE_ROW} ${locked ? 'cursor-default bg-muted/40' : 'cursor-pointer'}`}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    aria-label={c.name}
                                    checked={on}
                                    disabled={locked}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleCamera(c.id)}
                                    className="h-4 w-4 rounded border-border text-[var(--brand)] focus:ring-[var(--brand)] disabled:opacity-100"
                                  />
                                </td>
                                <td className={`max-w-0 truncate px-3 py-2 ${locked ? 'text-muted-foreground' : 'text-foreground'}`}>
                                  {c.name}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right text-[11px] text-muted-foreground">
                                  {locked ? (
                                    <span className="inline-flex items-center gap-1"><LockClosedIcon className="h-3 w-3" /> via group</span>
                                  ) : on ? (
                                    <span className="font-medium text-brand">Direct</span>
                                  ) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="shrink-0 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                      Directly-granted cameras add on top of group access. Cameras marked
                      <LockClosedIcon className="mx-1 inline h-3 w-3 align-text-bottom" />
                      <span className="font-medium">via group</span> are already covered by an assigned group.
                    </p>
                  </div>
                </div>
              )}
            </FillHeightCard>
          )}
        </div>
      </PageWithFilters>
      )}
    </>
  );
}
